/**
 * 시설 관리 통합 컴포넌트
 * 
 * 이 컴포넌트는 시설 관리의 중심 역할을 합니다.
 * UI 개선 계획에 따라 PoolDetail.tsx와 PoolManagement.tsx의 기능을 통합했습니다.
 * 
 * 주요 기능:
 * 1. 시설 기본 정보 관리 (PoolInfoManagement)
 * 2. CCTV 관리 (CCTVManagement)
 * 3. 웨어러블 장치 관리 (WearableManagement)
 * 4. 회원 관리 (MemberManagement)
 * 5. 모니터링 페이지 연결
 * 
 * URL 경로: /pool-management/:poolCode
 * 리디렉션: /pool/:code 또는 /pool/id/:poolId에서 이 페이지로 리디렉션됨
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
    CircularProgress,
    Alert,
    Grid,
  Paper,
    TextField,
    Button,
    Snackbar,
    Tabs,
    Tab,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
  TableContainer,
    Table,
  TableHead,
  TableRow,
    TableCell,
    TableBody,
  Chip,
    Tooltip,
    InputAdornment,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  Pool as PoolIcon,
  Info as InfoIcon,
  CameraOutdoor as CctvIcon,
  Watch as WearableIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
  VisibilityOff,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  MonitorHeartOutlined as MonitoringIcon,
  LiveTv as ControlMonitoringIcon,
  OpenInNew as OpenInNewIcon,
  Slideshow as SlideshowIcon,
  Assessment as AssessmentIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import {
  fetchMonitoringPlaybackRemote,
  fetchPoolCctvsForMonitoring,
  filterCentralMonitoringCctvs,
  normalizeCctvListFromApiPayload,
} from '../components/monitoring/monitoringPopupData';
import { api } from '../services/api';
import KymaBrandTitle from '../components/common/KymaBrandTitle';
import {
  openPoolMonitoringInNewWindow,
  openPoolMonitoringRotationInNewWindow,
  readMonitoringRotationIntervalSec,
} from '../components/monitoring/openPoolMonitoringWindow';
import type { PoolMonitoringCctv } from '../components/monitoring/monitoringPopupHtml';
import type { 
    Pool, 
    PoolCreate, 
    PoolUpdate, 
  PoolFilter
} from '../types/domain'; 
import type { ApiResponse } from '../types/api'; 
import { useApiService } from '../hooks/useApiService';
import standardPoolApi from '../services/standardPoolApi';
import CCTVManagement from '../components/cctv/CCTVManagement';
import WearableManagement from '../components/pool/WearableManagement';
import PoolInfoManagement from '../components/pool/PoolInfoManagement';
import MemberManagement from '../components/pool/MemberManagement';
import PoolAlertDeviceManagement from '../components/pool/PoolAlertDeviceManagement';
import EventLogWidget from '../components/dashboard/EventLogWidget';
import SafetyAlertDispatchHistoryCard from '../components/safety/SafetyAlertDispatchHistoryCard';
import PoolReportsPanel from './reports/PoolReportsPanel';
import { FacilityBoardPanel } from './FacilityBoardPanel';
import { API_ORIGIN } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useKeepGrantedTab } from '../brand/MenuPolicyContext';
import { POOL_MGMT_TAB_IDS, hiddenTabProps } from '../brand/menuCatalog';
import { axiosInstance } from '../services/axiosInstance';
import type { AlertDeviceLinkMode } from '../services/poolAlertDeviceService';
import {
  isPoolSiteStandaloneView,
  POOL_MANAGEMENT_TAB_MONITORING,
  poolMonitoringPath,
  poolSiteMonitoringState,
  poolSiteShortPath,
} from '../utils/poolSitePaths';
import { usePoolSitePageChrome } from '../hooks/usePoolSitePageChrome';

// API 기본 URL 정의
const API_URL = API_ORIGIN;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pool-tabpanel-${index}`}
      aria-labelledby={`pool-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `pool-tab-${index}`,
    'aria-controls': `pool-tabpanel-${index}`,
  };
}

const PoolManagement: React.FC = () => {
  const { poolCode } = useParams<{ poolCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const isAdminLoggedIn = Boolean(
    authState.isAdminPasswordVerified && authState.isAuthenticated,
  );
  const siteStandalone = isPoolSiteStandaloneView(location.pathname, isAdminLoggedIn);
  const [activeTab, setActiveTab] = useState(0);
  const facilityTabOn = useKeepGrantedTab(activeTab, POOL_MGMT_TAB_IDS, setActiveTab);
  const [pool, setPool] = useState<Pool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [managementCctvs, setManagementCctvs] = useState<PoolMonitoringCctv[]>([]);
  const [useRemotePlayback, setUseRemotePlayback] = useState(false);
  const [playbackConfigReady, setPlaybackConfigReady] = useState(false);
  const [rotationIntervalSec] = useState(() => readMonitoringRotationIntervalSec());

  usePoolSitePageChrome({
    poolName: pool?.name,
    poolCode,
    pageFallback: '시설 관리',
  });

  // 시설 정보 로드
  useEffect(() => {
    if (poolCode) {
      setIsLoading(true);
      setError(null);
      
      standardPoolApi.getPoolByCode(poolCode)
        .then(response => {
          if (response.success && response.data) {
            setPool(response.data);
                } else {
            setError(`시설 정보를 불러오기 실패: ${response.error || '알 수 없는 오류'}`);
          }
        })
        .catch(err => {
          console.error('시설 정보 로드 중 오류:', err);
          setError('시설 정보를 불러오기 실패');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [poolCode]);

  useEffect(() => {
    const loadPlaybackConfig = async () => {
      try {
        const remote = await fetchMonitoringPlaybackRemote();
        setUseRemotePlayback(remote);
      } catch {
        setUseRemotePlayback(false);
      } finally {
        setPlaybackConfigReady(true);
      }
    };
    void loadPlaybackConfig();
  }, []);

  useEffect(() => {
    if (!pool?.id) {
      setManagementCctvs([]);
      return;
    }
    let cancelled = false;
    const loadCctvs = async () => {
      try {
        const filtered = await fetchPoolCctvsForMonitoring(pool.id!);
        if (!cancelled) setManagementCctvs(filtered);
      } catch {
        if (!poolCode) {
          if (!cancelled) setManagementCctvs([]);
          return;
        }
        try {
          const response = await api.get(`/api/pools/code/${poolCode}/cctvs`);
          const list = normalizeCctvListFromApiPayload(response.data);
          if (!cancelled) setManagementCctvs(filterCentralMonitoringCctvs(list));
        } catch {
          if (!cancelled) setManagementCctvs([]);
        }
      }
    };
    void loadCctvs();
    return () => {
      cancelled = true;
    };
  }, [pool?.id, poolCode]);

  useEffect(() => {
    import('../components/monitoring/openPoolMonitoringWindow').then((mod) => {
      window.__getMonitoringPopupWindowFeatures = () => mod.getMonitoringPopupWindowFeatures();
      window.__placeMonitoringPopup = (popup: Window) => {
        mod.focusMonitoringPopupWindow(popup);
        void mod.placeMonitoringPopupOnPreferredScreen(popup).then(() => {
          mod.focusMonitoringPopupWindow(popup);
        });
      };
    });
    return () => {
      delete window.__getMonitoringPopupWindowFeatures;
      delete window.__placeMonitoringPopup;
    };
  }, []);

  const openMonitoringPopupPool = () => {
    if (!pool?.id || !pool.code) return null;
    const siteGw = Boolean(
      (pool as Pool & { site_gateway_enabled?: boolean }).site_gateway_enabled
    );
    return {
      id: pool.id,
      code: pool.code,
      name: pool.name,
      status: pool.status,
      site_gateway_enabled: siteGw,
    };
  };

  const handleOpenManagementCctvGrid = () => {
    if (!playbackConfigReady) {
      showSnackbar('재생 설정을 불러오는 중. 잠시 후 다시 시도', 'warning');
      return;
    }
    const popupPool = openMonitoringPopupPool();
    if (!popupPool) return;
    if (managementCctvs.length === 0) {
      showSnackbar('표시할 관제 CCTV가 없음', 'warning');
      return;
    }
    const siteGw = Boolean(popupPool.site_gateway_enabled);
    openPoolMonitoringInNewWindow(popupPool, managementCctvs, {
      skipPurposeFilter: true,
      useRemotePlayback,
      siteGatewayEnabled: siteGw,
      rotationIntervalSec,
      navigate,
    });
  };

  const handleOpenManagementCctvRotation = () => {
    if (!playbackConfigReady) {
      showSnackbar('재생 설정을 불러오는 중. 잠시 후 다시 시도', 'warning');
      return;
    }
    const popupPool = openMonitoringPopupPool();
    if (!popupPool) return;
    if (managementCctvs.length === 0) {
      showSnackbar('표시할 관제 CCTV가 없음', 'warning');
      return;
    }
    const siteGw = Boolean(popupPool.site_gateway_enabled);
    openPoolMonitoringRotationInNewWindow(popupPool, managementCctvs, {
      skipPurposeFilter: true,
      useRemotePlayback,
      siteGatewayEnabled: siteGw,
      rotationIntervalSec,
      navigate,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === POOL_MANAGEMENT_TAB_MONITORING && poolCode) {
      if (siteStandalone) {
        navigate(poolSiteShortPath(poolCode), { state: poolSiteMonitoringState() });
        return;
      }
      navigate(poolMonitoringPath(poolCode, false));
      return;
    }
    setActiveTab(newValue);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setIsSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setIsSnackbarOpen(false);
  };

  const handleRefreshData = () => {
    if (poolCode) {
      setIsLoading(true);
      standardPoolApi.getPoolByCode(poolCode)
        .then(response => {
          if (response.success && response.data) {
            setPool(response.data);
          } else {
            setError(`시설 정보를 불러오기 실패: ${response.error || '알 수 없는 오류'}`);
          }
        })
        .catch(err => {
          console.error('시설 정보 갱신 중 오류:', err);
          setError('시설 정보를 불러오기 실패');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleAlertDeviceLinkModeChange = async (mode: AlertDeviceLinkMode) => {
    if (!poolCode) throw new Error('시설 코드 없음');
    await axiosInstance.put(`/api/pools/code/${poolCode}`, {
      alert_device_link_mode: mode,
    });
    setPool((prev) => (prev ? { ...prev, alert_device_link_mode: mode } : prev));
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await standardPoolApi.deletePool(id);
      if (response.success) {
        showSnackbar('시설이 삭제 완료', 'success');
        navigate('/pools'); // 삭제 후 시설 목록 페이지로 이동
      } else {
        showSnackbar(`삭제 실패: ${response.message || '알 수 없는 오류'}`, 'error');
      }
    } catch (error) {
      console.error('시설 삭제 중 오류:', error);
      showSnackbar('시설 삭제 중 오류 발생', 'error');
    }
  };

  // Pool의 id 타입이 string | number일 수 있으므로, 타입 변환 함수 추가
  const getPoolIdAsNumber = (id: string | number | undefined): number | undefined => {
    if (id === undefined || id === null || id === '') return undefined;
    const n = typeof id === 'string' ? parseInt(id, 10) : Number(id);
    return Number.isFinite(n) ? n : undefined;
  };

  const getPoolIdAsString = (id: string | number | undefined): string | undefined => {
    if (id === undefined) return undefined;
    return typeof id === 'number' ? id.toString() : id;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
          </Box>
    );
  }

  if (error) {
      return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        </Box>
      );
    }
    
    return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: { xs: 1, sm: 2, md: 3 }, boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: 'wrap',
          mb: 2,
          gap: { xs: 1, sm: 2 },
          minWidth: 0,
        }}
      >
        <KymaBrandTitle variant="central" tone="default" size="medium" showTagline={false} />
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem', md: '2.125rem' }, wordBreak: 'break-word' }}
        >
          시설 관리 - {pool?.name || poolCode}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
          관제 CCTV
        </Typography>
        <Tooltip title="관제 CCTV를 현재 창 전체화면 그리드로 표시">
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenManagementCctvGrid}
          >
            Ai 관제 화면
          </Button>
        </Tooltip>
        <Tooltip title={`관제 CCTV 순환 보기 (${rotationIntervalSec}초) — 현재 창`}>
          <Button
            variant="contained"
            color="secondary"
            size="medium"
            startIcon={<SlideshowIcon />}
            onClick={handleOpenManagementCctvRotation}
          >
            CCTV 순환보기
          </Button>
        </Tooltip>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="pool management tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<ControlMonitoringIcon />} label="관제 이벤트" {...a11yProps(0)} {...hiddenTabProps(facilityTabOn(0))} />
            <Tab icon={<InfoIcon />} label="시설 정보" {...a11yProps(1)} {...hiddenTabProps(facilityTabOn(1))} />
            <Tab icon={<CctvIcon />} label="CCTV 관리" {...a11yProps(2)} {...hiddenTabProps(facilityTabOn(2))} />
            <Tab icon={<WarningIcon />} label="경광등/부저" {...a11yProps(3)} {...hiddenTabProps(facilityTabOn(3))} />
            <Tab icon={<WarningIcon />} label="경광등/부저 A" {...a11yProps(4)} {...hiddenTabProps(facilityTabOn(4))} />
            <Tab icon={<WearableIcon />} label="웨어러블 관리" {...a11yProps(5)} {...hiddenTabProps(facilityTabOn(5))} />
            <Tab icon={<PersonIcon />} label="회원 관리" {...a11yProps(6)} {...hiddenTabProps(facilityTabOn(6))} />
            <Tab icon={<MonitoringIcon />} label="모니터링" {...a11yProps(7)} {...hiddenTabProps(facilityTabOn(7))} />
            <Tab icon={<AssessmentIcon />} label="리포트" {...a11yProps(8)} {...hiddenTabProps(facilityTabOn(8))} />
            <Tab icon={<ForumIcon />} label="시설 게시판" {...a11yProps(9)} {...hiddenTabProps(facilityTabOn(9))} />
            </Tabs>
          </Box>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EventLogWidget
                title="최근 이벤트"
                subtitle="이 시설에서 발생한 최근 이벤트"
                poolId={pool?.id != null ? String(pool.id) : undefined}
                maxItems={200}
                pageSize={10}
                showRefresh
                showFilter
                showViewAll={false}
              />
            </Grid>
            {pool?.id != null && Number.isFinite(Number(pool.id)) ? (
              <Grid item xs={12}>
                <SafetyAlertDispatchHistoryCard
                  poolId={Number(pool.id)}
                  poolName={pool.name}
                  showPoolColumn={false}
                  pageSize={5}
                  title="현장 안전 알림 발송 이력"
                  subheader="SMS · 카카오 알림톡 · 웨어러블 — 이 시설 발송 결과"
                />
              </Grid>
            ) : null}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PoolInfoManagement 
            poolId={getPoolIdAsNumber(pool?.id)} 
            poolCode={poolCode} 
            pool={pool} 
            showSnackbar={showSnackbar}
            onRefreshData={handleRefreshData}
            allowInfoEdit={isAdminLoggedIn}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index={2}>
          <CCTVManagement 
            poolId={getPoolIdAsString(pool?.id)} 
            poolCode={poolCode}
            siteGatewayEnabled={Boolean(pool?.site_gateway_enabled)}
            poolRiskMotionDefaults={pool?.risk_motion_defaults ?? null}
            poolRiskLandThresholds={pool?.risk_land_thresholds ?? null}
            poolAiDetectionConf={pool?.ai_detection_conf ?? null}
            poolAiDetectionMinConsecutiveHits={pool?.ai_detection_min_consecutive_hits ?? null}
            scenarioProfile={pool?.scenario_profile ?? 'pool'}
            onSnackbarMessage={(message, severity) => showSnackbar(message, severity)}
          />
        </TabPanel>
        
        <TabPanel value={activeTab} index={3}>
          <PoolAlertDeviceManagement
            poolId={getPoolIdAsNumber(pool?.id)}
            linkMode="client"
            activeLinkMode={(pool?.alert_device_link_mode as 'client' | 'tcp_server_a') || 'client'}
            onActiveLinkModeChange={handleAlertDeviceLinkModeChange}
            showSnackbar={showSnackbar}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <PoolAlertDeviceManagement
            poolId={getPoolIdAsNumber(pool?.id)}
            linkMode="tcp_server_a"
            activeLinkMode={(pool?.alert_device_link_mode as 'client' | 'tcp_server_a') || 'client'}
            onActiveLinkModeChange={handleAlertDeviceLinkModeChange}
            showSnackbar={showSnackbar}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <WearableManagement 
            poolId={getPoolIdAsNumber(pool?.id)}
            poolCode={poolCode}
            showSnackbar={showSnackbar}
            onRefreshData={handleRefreshData}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <MemberManagement 
            poolId={getPoolIdAsString(pool?.id)}
            showSnackbar={showSnackbar}
            onRefreshData={handleRefreshData}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            「모니터링」 탭을 선택하면 시설 모니터링 화면으로 이동.
          </Typography>
        </TabPanel>

        <TabPanel value={activeTab} index={8}>
          {pool?.id != null ? (
            <PoolReportsPanel
              poolId={Number(pool.id)}
              poolName={pool.name}
            />
          ) : null}
        </TabPanel>

        <TabPanel value={activeTab} index={9}>
          {pool?.id != null ? (
            <FacilityBoardPanel
              embedded
              poolScope
              poolId={Number(pool.id)}
              poolName={pool.name}
            />
          ) : null}
        </TabPanel>
        </Paper>

      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PoolManagement;